import { useCallback, useId, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { StoreResponse } from '@clarity/contract';
import { useScopeContext } from '../lib/scopeContext.js';
import { lifecycleOf } from '../lib/lifecycle.js';
import { useI18n } from '../lib/i18n.js';
import { SURFACE_BY_ID, type SurfaceId } from './nav.js';

/** The translator shape from {@link useI18n}, narrowed for the helpers below. */
type Translate = ReturnType<typeof useI18n>['t'];

/**
 * Branches above this count get a search box — the SAME threshold the
 * {@link ScopeSwitcher} uses (Req 2.3), so the two chooser surfaces feel
 * identical: a user who learned the switcher's "type to filter" habit finds it
 * here too.
 */
const SEARCH_THRESHOLD = 7;

/**
 * PickBranch (clarity-navigation-scope Req 4.2 / 4.5, design §5/§6) — the compact
 * chooser the {@link BranchOnlyGuard} renders when a BRANCH-ONLY surface (catalog
 * / onboarding) is opened at an All / Brand scope that covers MORE than one
 * branch. A branch-only surface is meaningless as an aggregate, so instead of
 * rendering one we prompt the user to narrow to a single branch; each option
 * links to `/c/:clientId/store/:branchId/<surface>`, where the surface then
 * renders for that one branch.
 *
 * It is modelled on the scope switcher's panel so the app feels consistent:
 *
 * - At an **All** scope the in-scope branches are **grouped by brand** (reusing
 *   `brandGroups`), each group a labelled sub-list — the same brand headers the
 *   switcher shows (Req 2.2 / 3.5) — so a multi-brand client reads as brands,
 *   not one long flat list. A **Brand** scope already covers one brand, so its
 *   branches render as a single flat list (no redundant header).
 * - When the in-scope set is larger than {@link SEARCH_THRESHOLD} a **search**
 *   box filters by name or `externalStoreId`, reusing the switcher's exact
 *   predicate and the shared `scope.search*` strings (Req 2.3).
 * - Each row shows name · `externalStoreId` · platform · a **lifecycle chip**
 *   (`lifecycleOf` + the `scope-chip` styles landed in task 4.3), its state
 *   carried by text (not colour alone) for WCAG AA.
 *
 * Its user-facing copy is bilingual via `useI18n` (`pickBranch.*` /
 * `scope.*`, EN + AR). The surface name is itself a translated nav label
 * (`nav.<surface>`), so the prompt reads naturally in both languages. The
 * "Pick a branch" heading is a stable contract the branch-only-routing tests
 * assert on — keep it.
 */
export function PickBranch({ surface }: { surface: SurfaceId }) {
  const { clientId } = useParams();
  const { scope, branches, brandGroups } = useScopeContext();
  const { t } = useI18n();

  const [query, setQuery] = useState('');
  const searchId = useId();
  const headingId = useId();

  const def = SURFACE_BY_ID[surface];
  // The surface's own translated nav label (e.g. "Catalog" / "الكتالوج").
  const surfaceLabel = t(def.labelKey as Parameters<typeof t>[0]);

  const showSearch = branches.length > SEARCH_THRESHOLD;

  const normalizedQuery = query.trim().toLowerCase();
  // The SAME filter the switcher uses (Req 2.3): name or `externalStoreId`.
  const matches = useCallback(
    (s: StoreResponse) =>
      !normalizedQuery ||
      (s.name ?? '').toLowerCase().includes(normalizedQuery) ||
      s.externalStoreId.toLowerCase().includes(normalizedQuery),
    [normalizedQuery],
  );

  // At All scope, group the in-scope branches by brand (the same brand set the
  // switcher shows). A brand group is kept when its name matches the search OR
  // any of its branches do — matching the switcher's "a hit on a branch keeps
  // its brand head" rule. A Brand scope covers one brand, so it renders flat.
  const groups = useMemo(() => {
    if (scope.kind !== 'all') return null;
    const inScope = new Set(branches.map((b) => b.id));
    return brandGroups
      .map((g) => ({
        brand: g.brand,
        branches: g.branches.filter((b) => inScope.has(b.id)),
      }))
      .filter((g) => g.branches.length > 0)
      .map((g) => {
        const brandHit =
          !normalizedQuery || g.brand.name.toLowerCase().includes(normalizedQuery);
        // When the brand name matched, keep all its branches; otherwise only the
        // branches that matched the query.
        return {
          brand: g.brand,
          branches: brandHit ? g.branches : g.branches.filter(matches),
        };
      })
      .filter((g) => g.branches.length > 0);
  }, [scope.kind, branches, brandGroups, normalizedQuery, matches]);

  // The flat (Brand-scope / fallback) list, filtered by the same predicate.
  const flat = useMemo(() => branches.filter(matches), [branches, matches]);

  // Did an active search hide every branch? (used for the no-match line)
  const totalShown = groups
    ? groups.reduce((n, g) => n + g.branches.length, 0)
    : flat.length;
  const noMatch = normalizedQuery.length > 0 && totalShown === 0;

  return (
    <section className="pick-branch" data-pick-branch={surface}>
      <div className="page-head">
        <div>
          <h2 id={headingId}>{t('pickBranch.title')}</h2>
          <p className="sub">{t('pickBranch.subtitle', { surface: surfaceLabel })}</p>
        </div>
      </div>

      <nav
        className="card pick-branch__panel"
        aria-labelledby={headingId}
        aria-label={t('pickBranch.aria', { surface: surfaceLabel })}
      >
        {showSearch && (
          <div className="pick-branch__search">
            <label htmlFor={searchId} className="sr-only">
              {t('scope.search')}
            </label>
            <input
              id={searchId}
              type="search"
              className="pick-branch__search-input"
              placeholder={t('scope.search.placeholder')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        )}

        {noMatch && <p className="pick-branch__no-match muted">{t('scope.search.empty')}</p>}

        {groups
          ? groups.map((group) => (
              <div key={group.brand.id} className="pick-branch__group">
                <p className="pick-branch__group-head muted">
                  {group.brand.name} · {group.brand.platformKey}
                </p>
                <BranchList
                  branches={group.branches}
                  clientId={clientId}
                  surface={surface}
                  t={t}
                />
              </div>
            ))
          : !noMatch && (
              <BranchList branches={flat} clientId={clientId} surface={surface} t={t} />
            )}
      </nav>
    </section>
  );
}

/**
 * One list of branch rows — a `<ul>` of links, each navigating to that branch's
 * store scope. Reused for both the per-brand groups (All scope) and the single
 * flat list (Brand scope). The link's `aria-label` and `href` are a contract the
 * branch-only-routing tests assert on (name · store id · lifecycle, and
 * `/c/:clientId/store/:id/<surface>`).
 */
function BranchList({
  branches,
  clientId,
  surface,
  t,
}: {
  branches: StoreResponse[];
  clientId: string | undefined;
  surface: SurfaceId;
  t: Translate;
}) {
  return (
    <ul className="pick-branch-list">
      {branches.map((branch) => {
        const lifecycle = lifecycleOf(branch);
        const lifecycleLabel = t(`scope.lifecycle.${lifecycle}` as Parameters<typeof t>[0]);
        const name = branch.name ?? branch.externalStoreId;
        return (
          <li key={branch.id}>
            <Link
              to={`/c/${clientId}/store/${branch.id}/${surface}`}
              className="pick-branch-row"
              aria-label={`${name} · ${branch.externalStoreId} · ${lifecycleLabel}`}
            >
              <span className="pick-branch-name">{name}</span>
              <span className="pick-branch-meta muted">
                {branch.externalStoreId} · {branch.platformKey}
              </span>
              <span
                className={`scope-chip scope-chip--${lifecycle}`}
                data-lifecycle={lifecycle}
              >
                {lifecycleLabel}
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
